import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Beyond Sure — collapsible "Thinking…" summary.
 *
 * Mirrors Claude's pattern: shows a quiet 1-line summary by default;
 * expanding it reveals the model's reasoning. We never expose raw
 * chain-of-thought — only the curated summary the model writes for
 * the user. Hidden by default to keep the surface calm.
 *
 * Phase 1 ships the basic shell; Phase 2 will polish (real markdown
 * rendering inside the body, longer reasoning streams, scroll-cap).
 */

interface ThinkingDropdownProps {
  summary: string;
  /** Optional title override for the collapsed pill. */
  label?: string;
}

export function ThinkingDropdown({
  summary,
  label = "Thinking",
}: ThinkingDropdownProps) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="thinking-dropdown">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="thinking-body"
        data-testid="thinking-toggle"
        className={[
          "inline-flex items-center gap-1.5",
          "rounded-full px-3 py-1 text-[12px] font-medium",
          "bg-[var(--color-ink-08)] text-[var(--color-ink-60)]",
          "hover:bg-[var(--color-ink-16)] hover:text-[var(--color-ink)]",
          "transition-colors duration-[var(--duration-fast)]",
        ].join(" ")}
      >
        <span
          className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--color-brand-deep)] bsure-pulse"
          aria-hidden
        />
        {label}
        <svg
          width="10"
          height="10"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden
          style={{
            transition: "transform 160ms ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            id="thinking-body"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div
              className={[
                "mt-2 rounded-[var(--radius-md)] border border-[var(--color-ink-08)]",
                "bg-[rgb(20_28_24_/_0.03)] px-3.5 py-2.5",
                "text-[13px] leading-[1.55] text-[var(--color-ink-60)] italic",
                "whitespace-pre-wrap",
              ].join(" ")}
            >
              {summary}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default ThinkingDropdown;
